import { Badge, BodyText, Icons, Tooltip } from "@/components/ui";
import { LanguageNames } from "@/constants/Languages";
import { usePartner } from "@/hooks/usePartner";
import { LimitedAccess } from "@/icons";
import { selectFallbackPartnerUid } from "@/utils/partnerLogo";
import { constants } from "@curai/js-fhir-tools";
import { Earth } from "lucide-react";
import type { PatientDemographics } from "../types/patientDemographics";

interface MembershipInfoProps {
  membership: PatientDemographics["membership"];
  limitedAccess?: boolean;
  hieOptIn?: boolean;
  preferredLanguage?: constants.LanguageCode;
  careNotEstablished?: boolean | null;
}

export const MembershipInfo = ({
  membership,
  limitedAccess = false,
  preferredLanguage = constants.LanguageCode.ENGLISH,
  careNotEstablished = false,
}: MembershipInfoProps) => {
  const { partner } = usePartner(
    membership?.preferredPartnerUid ??
      selectFallbackPartnerUid(membership?.partnerUids),
  );

  const showLanguage = preferredLanguage !== constants.LanguageCode.ENGLISH;
  const languageDisplay = LanguageNames[preferredLanguage];

  return (
    <div className="flex items-center gap-3">
      {careNotEstablished && (
        <Badge variant="error-contrast">
          <Icons.VideoOff className="h-2 w-2 stroke-white" />
          <BodyText textColor="white" size="sm">
            <Tooltip
              side="bottom"
              tooltipContentText="Care not established: Perform AV call"
            >
              <span>Care Not Established</span>
            </Tooltip>
          </BodyText>
        </Badge>
      )}
      {limitedAccess && (
        <Tooltip side="bottom" tooltipContentText="Limited Access">
          <LimitedAccess
            data-testid="limited-access-icon"
            className="h-4 w-4"
          />
        </Tooltip>
      )}
      {partner && (
        <Tooltip
          side="bottom"
          key={partner.partnerUid}
          tooltipContentText={partner.name}
        >
          <img
            src={partner.logoUrl}
            alt={partner.name}
            className="w-4 h-4 min-w-4 min-h-4"
            data-testid={`${partner.partnerUid}-icon`}
          />
        </Tooltip>
      )}
      {showLanguage && (
        <Tooltip
          side="bottom"
          tooltipContentText={`Preferred Language: ${languageDisplay}`}
        >
          <Earth data-testid="earth-icon" className="h-4 w-4 stroke-info" />
        </Tooltip>
      )}
    </div>
  );
};
